import {
  RadialLinearScale,
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
  ChartArea,
} from 'chart.js'
import React, { useMemo } from 'react'
import { PolarArea } from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { formatNumber } from '../../lib/utils/formatNumber'
import { ContextData } from '@floating-ui/react'
import { isPDFGenerationModeEnabled } from '../../lib/utils/isPDFGenerationModeEnabled'

ChartJS.defaults.font.family = 'Spartan'
ChartJS.register(RadialLinearScale, ArcElement, Tooltip, Legend, ChartDataLabels)

const availableColors = [
  '#F2709C',
  '#4776E6',
  'rgba(255, 201, 12, 0.5)',
  'rgba(13, 189, 227, 0.5)',
  'rgba(142, 84, 233, 0.6)',
]

const getRedGradient = (
  ctx: ContextData,
  chartArea: ChartArea,
  raw: number,
): ContextData => {
  const centerX = (chartArea.left + chartArea.right) / 2
  const centerY = (chartArea.top + chartArea.bottom) / 2
  const r = Math.min(
    (chartArea.right - chartArea.left) / 2,
    (chartArea.bottom - chartArea.top) / 2,
  )
  const gradient = ctx.createRadialGradient(
    centerX,
    centerY,
    0,
    centerX,
    centerY,
    r * raw,
  )
  gradient.addColorStop(0.025, 'rgba(255, 148, 114, 0.49)')
  gradient.addColorStop(0.1, '#F2709C')
  return gradient
}

const getBlueGradient = (
  ctx: ContextData,
  chartArea: ChartArea,
  raw: number,
): ContextData => {
  const centerX = (chartArea.left + chartArea.right) / 2
  const centerY = (chartArea.top + chartArea.bottom) / 2
  const r = Math.min(
    (chartArea.right - chartArea.left) / 2,
    (chartArea.bottom - chartArea.top) / 2,
  )
  const gradient = ctx.createRadialGradient(
    centerX,
    centerY,
    0,
    centerX,
    centerY,
    r * raw,
  )
  gradient.addColorStop(0.025, 'rgba(142, 84, 233, 0.56)')
  gradient.addColorStop(0.1, '#4776E6')
  return gradient
}

const getYellowGradient = (
  ctx: ContextData,
  chartArea: ChartArea,
  raw: number,
): ContextData => {
  const centerX = (chartArea.left + chartArea.right) / 2
  const centerY = (chartArea.top + chartArea.bottom) / 2
  const r = Math.min(
    (chartArea.right - chartArea.left) / 2,
    (chartArea.bottom - chartArea.top) / 2,
  )
  const gradient = ctx.createRadialGradient(
    centerX,
    centerY,
    0,
    centerX,
    centerY,
    r * raw,
  )
  gradient.addColorStop(0.025, 'rgba(255, 245, 8, 0.17)')
  gradient.addColorStop(0.1, 'rgba(255, 201, 12, 0.5)')
  return gradient
}

const getCyanGradient = (
  ctx: ContextData,
  chartArea: ChartArea,
  raw: number,
): ContextData => {
  const centerX = (chartArea.left + chartArea.right) / 2
  const centerY = (chartArea.top + chartArea.bottom) / 2
  const r = Math.min(
    (chartArea.right - chartArea.left) / 2,
    (chartArea.bottom - chartArea.top) / 2,
  )
  const gradient = ctx.createRadialGradient(
    centerX,
    centerY,
    0,
    centerX,
    centerY,
    r * raw,
  )
  gradient.addColorStop(0.025, 'rgba(13, 227, 176, 0.16)')
  gradient.addColorStop(0.1, 'rgba(13, 189, 227, 0.5)')
  return gradient
}

const getPurpleGradient = (
  ctx: ContextData,
  chartArea: ChartArea,
  raw: number,
): ContextData => {
  const centerX = (chartArea.left + chartArea.right) / 2
  const centerY = (chartArea.top + chartArea.bottom) / 2
  const r = Math.min(
    (chartArea.right - chartArea.left) / 2,
    (chartArea.bottom - chartArea.top) / 2,
  )
  const gradient = ctx.createRadialGradient(
    centerX,
    centerY,
    0,
    centerX,
    centerY,
    r * raw,
  )
  gradient.addColorStop(0.025, 'rgba(0, 56, 255, 0.4)')
  gradient.addColorStop(0.1, 'rgba(142, 84, 233, 0.6)')
  return gradient
}

const gradients = [
  getRedGradient,
  getBlueGradient,
  getYellowGradient,
  getCyanGradient,
  getPurpleGradient,
]
const disableAnimations = isPDFGenerationModeEnabled()
  ? {
      animation: false,
    }
  : {}

const options: ChartOptions = {
  ...disableAnimations,
  responsive: true,
  elements: {
    line: {
      tension: 0.5,
    },
    point: {
      radius: 0,
    },
  },
  scales: {
    r: {
      pointLabels: {
        display: false,
      },
      ticks: {
        display: false, // Remove vertical numbers
      },
    },
  },
  maintainAspectRatio: false,
  plugins: {
    datalabels: {
      display: true,
      color: '#FFF',
      font: {
        size: 12,
        weight: 'bold',
      },
      formatter: (_, context) => {
        // @ts-ignore
        const rawValue = context.dataset.rawData?.[context.dataIndex] ?? 0
        return formatNumber(rawValue)
      },
      anchor: 'end',
      align: 'start',

      clamp: true,
      textAlign: 'center',
    },
    legend: {
      position: 'right',
      title: {
        display: false,
      },
      labels: {
        generateLabels: function (chart) {
          return (
            chart?.data?.labels?.map((label, index) => {
              return {
                text: (label as { label: string }).label,
                fillStyle: availableColors[index % availableColors.length],
                strokeStyle: availableColors[index % availableColors.length],
              }
            }) ?? []
          )
        },
      },
    },
    // @ts-expect-error
    crosshair: false,
    tooltip: {
      callbacks: {
        label: function (context) {
          return `${(context.label as any).label}: ${formatNumber(
            (context.label as any).value,
          )}`
        },
      },
      displayColors: false,
    },
  },
}

type Props = {
  data: Array<{ label: string; value: number }>
  isLoading?: boolean
  isPreviousData?: boolean
  chartScale?: 'log' | 'linear'
  customOptions?: Record<string, unknown>
}

const PolarAreaChart = ({
  data = [],
  isLoading = false,
  isPreviousData,
  chartScale = 'log',
  customOptions,
}: Props): JSX.Element => {
  const processedData = useMemo(() => {
    const originalValues = data
      .filter((d) => Number(d.value) > 0)
      .map((item) => item.value)
    const transformedValues = originalValues
      .filter((value) => Number(value) > 0)
      .reduce((acc: Array<number>, cur, index, array) => {
        const value = Number(cur)
        if (chartScale === 'linear') return [...acc, value]
        const numerator = value > 0 ? (value <= 1 ? 1 : Math.log(value)) : 0
        const denominator = Math.log(Math.max(...array, 1)) || 0
        const scale = numerator / (denominator || numerator)
        return [...acc, scale]
      }, [])

    return {
      originalData: data,
      labels: data
        .filter((d) => Number(d.value) > 0)
        .map((item) => ({
          label: item.label,
          value: item.value,
        })),
      datasets: [
        {
          data: transformedValues,
          rawData: originalValues,
          backgroundColor: (context: ContextData) => {
            const index = context.dataIndex
            return gradients[index % gradients.length](
              context.chart.ctx,
              context.chart.chartArea,
              context.raw,
            )
          },
        },
      ],
    }
  }, [chartScale, data])

  if (isLoading) {
    return (
      <div className={'w-full h-full flex items-center justify-center'}>Loading...</div>
    )
  }

  if (data.length === 0) {
    return (
      <div className={'w-full h-full flex items-center justify-center opacity-50'}>
        {/* @ts-ignore */}
        <PolarArea options={options} data={{ labels: [], datasets: [] }} />
      </div>
    )
  }

  return (
    <div className={'relative h-full w-full'}>
      {isPreviousData && (
        <div className={'absolute top-0 left-0 w-full h-full bg-white opacity-50'} />
      )}
      <PolarArea
        // @ts-ignore
        options={{ ...options, plugins: { ...options.plugins, ...customOptions } }}
        // @ts-ignore
        data={processedData}
      />
    </div>
  )
}

export default PolarAreaChart
